import React, { useState, useCallback } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import myAxios from "../../_services/axiositeachWrapper";
import Config from "../../_config/config";
import VoilaBoardService from "../../_services/VoilaBoardService";
import AddItemModal from "./components/AddItemModal";
import ShareSettingsModal from "./components/ShareSettingsModal";
import ItemCard from "./components/ItemCard";
import { THEME_PRESETS, getThemeById } from "./theme-presets";
import "./voila-board.css";

interface Board {
  id: number;
  boardUUID: string;
  enquiryId: number;
  enrollmentId: number | null;
  title: string | null;
  description: string | null;
  coverImageUrl: string | null;
  themeConfig: any;
  isPublished: number;
  sharePIN: string | null;
  shareExpiryDate: string | null;
  viewCount: number;
  items?: any[];
}

type View = "search" | "boards" | "editor";

const VoilaBoard: React.FC = () => {
  // Navigation state
  const [view, setView] = useState<View>("search");

  // Student search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [searching, setSearching] = useState(false);

  // Board list
  const [boards, setBoards] = useState<Board[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);

  // Board editor
  const [currentBoard, setCurrentBoard] = useState<Board | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");

  // Modals
  const [showAddItem, setShowAddItem] = useState(false);
  const [showShareSettings, setShowShareSettings] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState("");

  // Delete confirmation
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);

  // Theme
  const [selectedThemeId, setSelectedThemeId] = useState("classic");

  // Auto-populate feedback
  const [autoPopMsg, setAutoPopMsg] = useState("");

  // ─── Student Search ───
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const searchRes = await myAxios.get<any>(`${Config.nodeSiteUrl}v1/credentials/admin/search-students`, {
        params: { search: searchQuery },
      });
      setSearchResults(searchRes.data?.data || []);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setSearching(false);
    }
  };

  const selectStudent = async (student: any) => {
    setSelectedStudent(student);
    setView("boards");
    await loadBoards(student.enquiryId);
  };

  // ─── Board List ───
  const loadBoards = useCallback(async (enquiryId: number) => {
    setLoadingBoards(true);
    try {
      const res = await VoilaBoardService.listBoards(enquiryId);
      setBoards(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load boards:", err);
    } finally {
      setLoadingBoards(false);
    }
  }, []);

  const handleCreateBoard = async () => {
    if (!selectedStudent) return;
    try {
      await VoilaBoardService.createBoard({
        enquiryId: selectedStudent.enquiryId,
        title: newBoardTitle || `${selectedStudent.studentName}'s Board`,
      });
      setShowCreateModal(false);
      setNewBoardTitle("");
      await loadBoards(selectedStudent.enquiryId);
    } catch (err) {
      console.error("Failed to create board:", err);
    }
  };

  // ─── Board Editor ───
  const openBoard = async (board: Board) => {
    try {
      const res = await VoilaBoardService.getBoard(board.id);
      const fullBoard = res.data?.data;
      setCurrentBoard(fullBoard);
      setEditTitle(fullBoard?.title || "");
      setEditDesc(fullBoard?.description || "");
      setSelectedThemeId(fullBoard?.themeConfig?.themeId || "classic");
      setView("editor");
    } catch (err) {
      console.error("Failed to load board:", err);
    }
  };

  const refreshBoard = async () => {
    if (!currentBoard) return;
    try {
      const res = await VoilaBoardService.getBoard(currentBoard.id);
      setCurrentBoard(res.data?.data);
    } catch (err) {
      console.error("Failed to refresh board:", err);
    }
  };

  const handleSaveBoard = async () => {
    if (!currentBoard) return;
    try {
      await VoilaBoardService.updateBoard(currentBoard.id, {
        title: editTitle,
        description: editDesc,
      });
      await refreshBoard();
    } catch (err) {
      console.error("Failed to update board:", err);
    }
  };

  const handleDeleteItem = async () => {
    if (!currentBoard || deleteItemId === null) return;
    try {
      await VoilaBoardService.deleteItem(currentBoard.id, deleteItemId);
      setDeleteItemId(null);
      await refreshBoard();
    } catch (err) {
      console.error("Failed to delete item:", err);
    }
  };

  const handleTogglePin = async (itemId: number, currentPinned: number) => {
    if (!currentBoard) return;
    try {
      await VoilaBoardService.updateItem(currentBoard.id, itemId, {
        isPinned: !currentPinned,
      });
      await refreshBoard();
    } catch (err) {
      console.error("Failed to toggle pin:", err);
    }
  };

  const handleAutoPopulate = async () => {
    if (!currentBoard) return;
    setAutoPopMsg("Scanning for badges, achievements, milestones...");
    try {
      const res = await VoilaBoardService.autoPopulate(currentBoard.id);
      const added = res.data?.data?.added || 0;
      setAutoPopMsg(`Added ${added} item${added !== 1 ? "s" : ""} from existing data.`);
      await refreshBoard();
      setTimeout(() => setAutoPopMsg(""), 3000);
    } catch (err) {
      setAutoPopMsg("Auto-populate failed.");
      console.error("Auto-populate failed:", err);
    }
  };

  // ─── Render ───
  return (
    <div className="voila-board-page">
      {/* Search View */}
      {view === "search" && (
        <>
          <h4>Voila Board</h4>
          <p className="text-muted">Search for a student to create or manage their boards.</p>
          <div className="d-flex gap-2 mb-3" style={{ maxWidth: 500 }}>
            <Form.Control
              placeholder="Name, roll no, email, mobile, student code, or enquiry ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button variant="primary" onClick={handleSearch} disabled={searching}>
              {searching ? "..." : "Search"}
            </Button>
          </div>
          {searchResults.length > 0 && (
            <div>
              {searchResults.map((s: any) => (
                <div
                  key={s.enquiryId}
                  className="board-card"
                  onClick={() => selectStudent(s)}
                >
                  {s.photoPath ? (
                    <img src={s.photoPath} alt="" className="board-card-cover" />
                  ) : (
                    <div className="board-card-cover-placeholder">
                      {(s.studentName || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="board-card-info">
                    <h5>{s.studentName}</h5>
                    <p>{[s.rollNumber, s.studentCode, s.email, s.mobile1 || s.mobile2].filter(Boolean).join(" | ")}</p>
                    <div className="board-card-meta">ID: {s.enquiryId}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Board List View */}
      {view === "boards" && selectedStudent && (
        <>
          <div className="d-flex align-items-center gap-3 mb-3">
            <Button variant="secondary" size="sm" onClick={() => { setView("search"); setSelectedStudent(null); }}>
              &larr; Back
            </Button>
            <h4 className="mb-0">{selectedStudent.studentName}'s Boards</h4>
          </div>

          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + Create New Board
          </Button>

          <div className="board-list-container">
            {loadingBoards ? (
              <p>Loading...</p>
            ) : boards.length === 0 ? (
              <p className="text-muted mt-3">No boards yet. Create one to get started!</p>
            ) : (
              boards.map((b) => (
                <div key={b.id} className="board-card" onClick={() => openBoard(b)}>
                  {b.coverImageUrl ? (
                    <img src={b.coverImageUrl} alt="" className="board-card-cover" />
                  ) : (
                    <div className="board-card-cover-placeholder">
                      {(b.title || "V")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="board-card-info">
                    <h5>{b.title || "Untitled Board"}</h5>
                    <p>{b.description || ""}</p>
                    <div className="board-card-meta">
                      {b.isPublished ? "Published" : "Draft"}
                      {b.viewCount > 0 && ` | ${b.viewCount} views`}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Create Board Modal */}
          <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Create New Board</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group>
                <Form.Label>Board Title</Form.Label>
                <Form.Control
                  value={newBoardTitle}
                  onChange={(e) => setNewBoardTitle(e.target.value)}
                  placeholder={`${selectedStudent.studentName}'s Journey`}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleCreateBoard}>Create</Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      {/* Board Editor View */}
      {view === "editor" && currentBoard && (
        <>
          <div className="d-flex align-items-center gap-3 mb-3">
            <Button variant="secondary" size="sm" onClick={() => { setView("boards"); setCurrentBoard(null); }}>
              &larr; Back
            </Button>
            <h4 className="mb-0">{currentBoard.title || "Untitled Board"}</h4>
          </div>

          {/* Board Settings */}
          <div className="d-flex gap-2 mb-3" style={{ maxWidth: 600 }}>
            <Form.Control
              placeholder="Board Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
            <Button variant="primary" size="sm" onClick={handleSaveBoard}>Save</Button>
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            className="mb-3"
            style={{ maxWidth: 600 }}
            placeholder="Board description..."
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
          />

          {/* Theme Picker */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, display: "block" }}>
              Board Theme
            </label>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
              {THEME_PRESETS.map((preset) => (
                <div
                  key={preset.id}
                  onClick={async () => {
                    setSelectedThemeId(preset.id);
                    if (!currentBoard) return;
                    try {
                      await VoilaBoardService.updateBoard(currentBoard.id, {
                        themeConfig: { themeId: preset.id },
                      });
                      await refreshBoard();
                    } catch (err) {
                      console.error("Failed to save theme:", err);
                    }
                  }}
                  style={{
                    width: 100,
                    height: 64,
                    borderRadius: 8,
                    background: preset.heroGradient,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    padding: 6,
                    cursor: "pointer",
                    flexShrink: 0,
                    border: selectedThemeId === preset.id
                      ? `3px solid ${preset.accentColor}`
                      : "2px solid transparent",
                    boxShadow: selectedThemeId === preset.id
                      ? `0 0 0 2px ${preset.accentColor}40`
                      : "none",
                  }}
                >
                  <span style={{
                    color: preset.heroTextColor,
                    fontSize: 11,
                    fontWeight: 600,
                    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                  }}>
                    {preset.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Button variant="primary" onClick={() => setShowAddItem(true)}>
              + Add Item
            </Button>
            <Button variant="secondary" onClick={handleAutoPopulate}>
              Auto-Populate
            </Button>
            <Button variant="secondary" onClick={() => setShowShareSettings(true)}>
              Share Settings
            </Button>
          </div>

          {autoPopMsg && (
            <p style={{ color: "var(--status-success)", fontSize: 14 }}>{autoPopMsg}</p>
          )}

          {/* Timeline Items */}
          <div className="timeline-container">
            {(currentBoard.items || []).length === 0 ? (
              <p className="text-muted">
                No items yet. Add photos, stories, or use Auto-Populate to pull in badges and achievements.
              </p>
            ) : (
              (currentBoard.items || []).map((item: any) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onDelete={(id) => setDeleteItemId(id)}
                  onTogglePin={handleTogglePin}
                />
              ))
            )}
          </div>

          {/* Add Item Modal */}
          <AddItemModal
            show={showAddItem}
            onHide={() => setShowAddItem(false)}
            boardId={currentBoard.id}
            onItemAdded={refreshBoard}
          />

          {/* Share Settings Modal */}
          <ShareSettingsModal
            show={showShareSettings}
            onHide={() => setShowShareSettings(false)}
            board={currentBoard}
            onUpdated={refreshBoard}
          />

          {/* Delete Confirmation Modal */}
          <Modal show={deleteItemId !== null} onHide={() => setDeleteItemId(null)}>
            <Modal.Header closeButton>
              <Modal.Title>Delete Item</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to remove this item from the board?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setDeleteItemId(null)}>Cancel</Button>
              <Button variant="danger" onClick={handleDeleteItem}>Delete</Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};

export default VoilaBoard;
